import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlugsConnected, Plus, Trash, Lightning, ArrowsClockwise, Broadcast, Plug, CheckCircle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Integrations() {
  const { t } = useTranslation();
  const { currentOrg } = useOrg();
  const [catalog, setCatalog] = useState([]);
  const [connections, setConnections] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ connector_id: "", name: "" });
  const [detail, setDetail] = useState(null);
  const [runs, setRuns] = useState([]);
  const [webhook, setWebhook] = useState(null);

  const load = () => {
    api.get("/integrations/connectors").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/integrations/connections").then((r) => setConnections(r.data)).catch(() => {});
  };
  useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

  const openCreate = (connectorId) => { setForm({ connector_id: connectorId, name: "" }); setOpen(true); };

  const create = async () => {
    try { await api.post("/integrations/connections", form); setOpen(false); load(); toast.success("Connection created"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const authorize = async (c) => {
    try {
      const { data } = await api.post(`/integrations/connections/${c.id}/oauth/start`);
      // Mock OAuth: extract state and complete immediately (real IdP would redirect the user).
      const state = new URL(data.authorize_url).searchParams.get("state");
      await api.post(`/integrations/connections/${c.id}/oauth/complete`, { code: "mock_code", state });
      toast.success("Authorized (mock OAuth2 + PKCE)");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const test = async (c) => {
    try { const { data } = await api.post(`/integrations/connections/${c.id}/test`); toast[data.ok ? "success" : "error"](data.ok ? "Connection OK" : "Not authorized"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const sync = async (c) => {
    try { const { data } = await api.post(`/integrations/connections/${c.id}/sync`); toast.success(`Synced ${data.records} records`); if (detail?.id === c.id) openDetail(c); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm("Delete this connection and its secrets?")) return;
    try { await api.delete(`/integrations/connections/${c.id}`); if (detail?.id === c.id) setDetail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    api.get(`/integrations/connections/${c.id}/syncs`).then((r) => setRuns(r.data)).catch(() => setRuns([]));
    api.get(`/integrations/connections/${c.id}/webhook`).then((r) => setWebhook(r.data)).catch(() => setWebhook(null));
  };

  return (
    <div>
      <PageHeader title={t("m3.title")} subtitle={t("m3.sub")} />

      {/* Catalog */}
      <h3 className="overline text-muted-foreground mb-3">{t("m3.catalog")}</h3>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {catalog.map((c) => (
          <div key={c.connector_id} className="bg-card border border-border rounded-sm p-6" data-testid={`connector-${c.connector_id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 bg-secondary flex items-center justify-center rounded-sm"><Plug size={20} weight="bold" /></div>
              <Badge variant="outline" className="rounded-sm text-[10px] uppercase font-mono">{c.auth_type}</Badge>
            </div>
            <h4 className="font-heading font-bold text-lg mt-4">{c.name}</h4>
            <div className="flex flex-wrap gap-1 mt-2">
              {c.capabilities.map((cap) => <span key={cap.id} className="font-mono text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm">{cap.id}</span>)}
            </div>
            <Button className="rounded-sm w-full mt-4" onClick={() => openCreate(c.connector_id)} data-testid={`add-connection-${c.connector_id}`}>
              <Plus size={16} className="mr-1" />{t("m3.newConnection")}
            </Button>
          </div>
        ))}
      </div>

      {/* Connections */}
      <h3 className="overline text-muted-foreground mb-3">{t("m3.connections")}</h3>
      {connections.length === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-12 text-center text-muted-foreground" data-testid="connections-empty">{t("m3.noConnections")}</div>
      ) : (
        <div className="bg-card border border-border rounded-sm overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("m3.connector")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {connections.map((c) => (
                <TableRow key={c.id} data-testid={`connection-row-${c.id}`} className="cursor-pointer" onClick={() => openDetail(c)}>
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell className="font-mono text-xs">{c.connector_id}</TableCell>
                  <TableCell>
                    <Badge variant={c.status === "connected" ? "default" : "secondary"} className="rounded-sm text-[10px] uppercase">
                      {c.status === "connected" ? <CheckCircle size={10} className="mr-1" /> : <Clock size={10} className="mr-1" />}{c.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      {c.status !== "connected" && <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => authorize(c)} data-testid={`authorize-${c.id}`}><Lightning size={14} className="mr-1" />{t("m3.authorize")}</Button>}
                      {c.status === "connected" && <>
                        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => test(c)} data-testid={`test-${c.id}`}>{t("m3.test")}</Button>
                        <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => sync(c)} data-testid={`sync-${c.id}`}><ArrowsClockwise size={14} className="mr-1" />{t("m3.sync")}</Button>
                      </>}
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(c)} data-testid={`delete-connection-${c.id}`}><Trash size={16} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="backdrop-blur-xl bg-background/80">
          <DialogHeader><DialogTitle className="font-heading">{t("m3.newConnection")}</DialogTitle><DialogDescription className="sr-only">Create a connector connection</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t("m3.connector")}</Label>
              <Select value={form.connector_id} onValueChange={(v) => setForm({ ...form, connector_id: v })}>
                <SelectTrigger className="rounded-sm" data-testid="connection-connector-select"><SelectValue placeholder="Select connector" /></SelectTrigger>
                <SelectContent>{catalog.map((c) => <SelectItem key={c.connector_id} value={c.connector_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="connection-name-input" /></div>
          </div>
          <DialogFooter><Button onClick={create} disabled={!form.connector_id || !form.name.trim()} data-testid="create-connection-submit">{t("common.create")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="backdrop-blur-xl bg-background/90 max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{detail?.name}</DialogTitle><DialogDescription className="font-mono text-xs">{detail?.connector_id} · {detail?.status}</DialogDescription></DialogHeader>
          {webhook && (
            <div className="border border-border rounded-sm p-4 space-y-2" data-testid="webhook-info">
              <div className="flex items-center gap-2 text-sm font-medium"><Broadcast size={16} className="text-primary" />{t("m3.webhook")}</div>
              <div className="text-xs"><span className="text-muted-foreground">{t("m3.endpointUrl")}:</span> <code className="font-mono break-all">{webhook.url}</code></div>
              <div className="text-xs"><span className="text-muted-foreground">{t("m3.secretHint")}:</span> <code className="font-mono">{webhook.secret}</code></div>
            </div>
          )}
          <div>
            <div className="overline text-muted-foreground mb-2">{t("m3.runs")}</div>
            <div className="border border-border rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>{t("common.status")}</TableHead><TableHead>{t("m3.records")}</TableHead><TableHead>Cursor</TableHead><TableHead>{t("m2.time")}</TableHead></TableRow></TableHeader>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow key={r.id} data-testid={`sync-run-${r.id}`}>
                      <TableCell><Badge variant={r.status === "completed" ? "default" : "destructive"} className="rounded-sm text-[10px] uppercase">{r.status}</Badge></TableCell>
                      <TableCell className="font-mono text-xs">{r.records ?? 0}</TableCell>
                      <TableCell className="font-mono text-xs">{r.cursor ?? "—"}</TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{(r.started_at || "").slice(0, 19).replace("T", " ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
